
import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { EncryptDecryptService } from '../services/encrypt-decrypt.service';

function shouldHandleRequest(req: HttpRequest<unknown>): boolean {
  return environment.enablePayloadEncryption && req.url.includes('/api/');
}

function isBinaryBody(body: unknown): boolean {
  return body instanceof FormData || body instanceof Blob || body instanceof ArrayBuffer;
}

function tryParseJson(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

@Injectable()
export class PayloadCryptoInterceptor implements HttpInterceptor {
  constructor(private cryptoService: EncryptDecryptService) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!shouldHandleRequest(req)) {
      return next.handle(req);
    }

    let encryptedReq = req.clone({
      setHeaders: { 'X-Payload-Encrypted': '1' },
    });

    if (req.body != null && !isBinaryBody(req.body)) {
      const rawBody = typeof req.body === 'string' ? req.body : JSON.stringify(req.body);
      encryptedReq = encryptedReq.clone({
        body: this.cryptoService.encryptData(rawBody),
      });
    }

    return next.handle(encryptedReq).pipe(
      map((event: HttpEvent<unknown>) => {
        if (event instanceof HttpResponse && this.cryptoService.isEncryptedEnvelope(event.body)) {
          const decrypted = this.cryptoService.decryptData(event.body.payload, event.body.iv);
          return event.clone({ body: tryParseJson(decrypted) });
        }
        return event;
      }),
      catchError((error: HttpErrorResponse) => {
        if (this.cryptoService.isEncryptedEnvelope(error.error)) {
          try {
            const decrypted = this.cryptoService.decryptData(error.error.payload, error.error.iv);
            const parsed = tryParseJson(decrypted);
            return throwError(() => new HttpErrorResponse({
              ...error,
              error: parsed,
              url: error.url ?? undefined,
            }));
          } catch {
            return throwError(() => error);
          }
        }
        return throwError(() => error);
      }),
    );
  }
}